import { useState } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { users, photos } from '@cwos/api-client';
import type { users as UsersNS } from '@cwos/api-client';
import { Avatar, Pill, SkeletonSet, SuccessEmpty, TableCard, type Column } from '@cwos/ui-admin';
import type { Severity } from '@cwos/ui-shared';
import { usePermissions } from '../permissions.js';
import { useNav } from '../navigation.js';
import { SettingsCard, SettingsField, SettingsHeader } from './common.js';
import { LoadFailure } from '../LoadFailure.js';

const STATUS_TONE: Record<UsersNS.AccountStatus, { variant: Severity; label: string }> = {
  active: { variant: 'good', label: 'Active' },
  invited: { variant: 'sky', label: 'Invited' },
  suspended: { variant: 'urgent', label: 'Suspended' },
  deactivated: { variant: 'plain', label: 'Deactivated' },
};

function statusTone(status: UsersNS.AccountStatus): { variant: Severity; label: string } {
  return STATUS_TONE[status] ?? { variant: 'plain', label: status };
}

function rolesLabel(p: UsersNS.Person): string {
  return p.roles.length ? p.roles.map((r) => r.name).join(' · ') : '—';
}

function workAssignment(p: UsersNS.Person): string {
  if (p.primaryProgramName) {
    return p.primaryProgramLocationName
      ? `${p.primaryProgramName} · ${p.primaryProgramLocationName}`
      : p.primaryProgramName;
  }
  return '—';
}

/**
 * People & roles (doc 01 A6, doc 12 §12.3.3, doc 13 T9/T10). Members + pending
 * invitations over the live `/users` + `/invitations` routes. Account lifecycle
 * lives on the person detail screen; here the list is a real read over the
 * directory/auth join — no fabricated rows.
 */
export function PeopleSettings() {
  const qc = useQueryClient();
  const { has } = usePermissions();
  const navigate = useNav();
  const canManage = has('user:manage');

  const list = useQuery({ queryKey: ['users', 'list'], queryFn: () => users.list() });
  const invitations = useQuery({
    queryKey: ['invitations', 'list'],
    queryFn: () => users.invitations(),
  });
  const roleOptions = useQuery({ queryKey: ['roles', 'list'], queryFn: () => users.roles() });

  /**
   * What the LAST resend answered, and which row it was for (F105).
   *
   * Resend used to say nothing at all. That was survivable only while the route
   * answered `delivery` alone and there was nothing honest to report; now that
   * it carries both facts, silence is its own over-claim — the administrator
   * pressed a button, the row still says the same thing, and nobody told them
   * that no email left the building. Keyed by invitation id so the answer
   * appears against the row it is about rather than floating over the list.
   */
  const [resendResult, setResendResult] = useState<{
    id: string;
    notice: EmailAttemptNotice;
    /** What the notice answers — it names the live region, so a revoke failure
     * must not announce itself as a "Resend result". */
    label?: string;
  } | null>(null);

  const revoke = useMutation({
    mutationFn: (id: string) => users.revokeInvitation(id),
    onSuccess: () => void qc.invalidateQueries({ queryKey: ['invitations', 'list'] }),
    // A failed revoke used to say nothing, and the row it failed on stayed on
    // screen looking exactly like one nobody had touched — the administrator
    // walks away believing a live invitation is dead. The failure names itself
    // on the row it is about (same mechanism as the resend notice).
    onError: (e: unknown, id) => {
      setResendResult({
        id,
        label: 'Revoke result',
        notice: {
          headline: 'The invitation was not revoked.',
          lines: [
            e instanceof Error ? e.message : 'The request did not go through.',
            'It is still pending, and its link still works.',
          ],
          bad: true,
        },
      });
    },
  });

  const resend = useMutation({
    mutationFn: (id: string) => users.resendInvitation(id),
    onSuccess: (result, id) => {
      setResendResult({ id, notice: resendNotice(result) });
      void qc.invalidateQueries({ queryKey: ['invitations', 'list'] });
    },
    onError: (e: unknown, id) => {
      setResendResult({
        id,
        notice: {
          headline: 'Resend failed.',
          lines: [e instanceof Error ? e.message : 'The request did not go through.'],
          bad: true,
        },
      });
    },
  });

  /**
   * Correcting a mistyped address (F95).
   *
   * `role:assign` as well as `user:manage`, because that is what the route
   * asks: repointing a pending invitation chooses which mailbox may redeem the
   * role it names. Gating the affordance on the same pair means a coordinator
   * holding `user:manage` alone does not meet a 403 on a button the screen
   * offered them — the server refuses either way, and this is the client
   * telling the same truth earlier.
   */
  const canCorrectAddress = canManage && has('role:assign');
  /** Which row's address form is open. One at a time: this is a repair, not a bulk edit. */
  const [correcting, setCorrecting] = useState<string | null>(null);

  const correctAddress = useMutation({
    mutationFn: (v: { id: string; email: string }) =>
      users.resendInvitationToCorrectedAddress(v.id, v.email),
    onSuccess: (result, v) => {
      setCorrecting(null);
      setResendResult({ id: v.id, notice: correctedAddressNotice(result) });
      void qc.invalidateQueries({ queryKey: ['invitations', 'list'] });
    },
    onError: (e: unknown, v) => {
      setResendResult({
        id: v.id,
        notice: {
          headline: 'The address was not changed.',
          lines: [
            e instanceof Error ? e.message : 'The request did not go through.',
            // Said explicitly: a failed correction must not leave anybody
            // wondering whether the old address is still live. It is.
            'This invitation still points at the address it did before.',
          ],
          bad: true,
        },
      });
    },
  });

  const columns: Column<UsersNS.Person>[] = [
    {
      key: 'name',
      header: 'Person',
      render: (p) => (
        <span style={{ display: 'inline-flex', alignItems: 'center', gap: 'var(--space-8)' }}>
          <Avatar
            name={p.name || p.email}
            size={26}
            src={photos.personPhotoSrc({ userId: p.id, photoUpdatedAt: p.photoUpdatedAt })}
          />
          {p.name || p.email}
        </span>
      ),
    },
    { key: 'roles', header: 'Role', render: (p) => rolesLabel(p) },
    {
      key: 'status',
      header: 'Status',
      render: (p) => {
        const tone = statusTone(p.status);
        return (
          <Pill variant={tone.variant} dot>
            {tone.label}
          </Pill>
        );
      },
    },
    {
      key: 'hours',
      header: 'Weekly hours',
      numeric: true,
      render: (p) => (p.expectedWeeklyHours != null ? String(p.expectedWeeklyHours) : '—'),
    },
    { key: 'work', header: 'Work assignment', render: (p) => workAssignment(p) },
    {
      key: 'lastlogin',
      header: 'Last active',
      render: (p) => (p.lastLoginAt ? new Date(p.lastLoginAt).toLocaleDateString() : '—'),
    },
  ];

  return (
    <div className="rn-col" style={{ maxWidth: 980 }}>
      <SettingsHeader
        title="People & roles"
        blurb="Everyone who signs in to this organization, and the invites still waiting."
      />

      <SettingsCard>
        <div className="rn-card-head">
          <h2 className="rn-card-title">Members</h2>
        </div>
        {list.isLoading ? (
          <SkeletonSet surface="table" rows={4} />
        ) : list.isError && !list.data ? (
          <LoadFailure
            error={list.error}
            message="We couldn’t load people just now."
            onRetry={() => void list.refetch()}
          />
        ) : list.data && list.data.length === 0 ? (
          <SuccessEmpty message="No members yet. Invite someone to get started." />
        ) : (
          <TableCard
            columns={columns}
            rows={list.data ?? []}
            rowKey={(p) => p.id}
            onRowOpen={(p) => navigate(`/settings/people/${p.id}`)}
          />
        )}
      </SettingsCard>

      <SettingsCard>
        <div className="rn-card-head">
          <h2 className="rn-card-title">Pending invitations</h2>
        </div>
        {invitations.isLoading ? (
          <SkeletonSet surface="table" rows={2} />
        ) : invitations.isError && !invitations.data ? (
          <LoadFailure
            error={invitations.error}
            message="We couldn’t load invitations just now."
            onRetry={() => void invitations.refetch()}
          />
        ) : invitations.data && invitations.data.length === 0 ? (
          <p className="rn-muted" style={{ marginBottom: 0 }}>
            No invitations waiting.
          </p>
        ) : (
          <ul
            style={{
              listStyle: 'none',
              margin: 0,
              padding: 0,
              display: 'grid',
              gap: 'var(--space-10)',
            }}
          >
            {(invitations.data ?? []).map((inv) => (
              <li
                key={inv.id}
                style={{
                  display: 'grid',
                  gridTemplateColumns: '1fr auto',
                  gap: 'var(--space-12)',
                  alignItems: 'center',
                  border: '1px solid var(--color-line)',
                  borderRadius: 'var(--radius-sm)',
                  padding: 'var(--space-12)',
                }}
              >
                <div>
                  <div style={{ fontWeight: 600 }}>
                    {inv.invitedName ? `${inv.invitedName} · ${inv.email}` : inv.email}
                  </div>
                  <div className="rn-muted">
                    {inv.roleName} · invited by {inv.invitedByName ?? inv.invitedByEmail ?? '—'} ·
                    expires {new Date(inv.expiresAt).toLocaleDateString()}
                  </div>
                  <DeliveryNote invitation={inv} />
                  {resendResult?.id === inv.id ? (
                    <NoticeBlock
                      notice={resendResult.notice}
                      label={resendResult.label ?? 'Resend result'}
                    />
                  ) : null}
                  {correcting === inv.id ? (
                    <CorrectAddressForm
                      currentEmail={inv.email}
                      pending={correctAddress.isPending}
                      onCancel={() => setCorrecting(null)}
                      onSubmit={(email) => correctAddress.mutate({ id: inv.id, email })}
                    />
                  ) : null}
                </div>
                {canManage ? (
                  <div style={{ display: 'flex', gap: 'var(--space-8)' }}>
                    <button
                      type="button"
                      className="rn-link"
                      disabled={resend.isPending}
                      onClick={() => resend.mutate(inv.id)}
                    >
                      Resend
                    </button>
                    {canCorrectAddress ? (
                      <button
                        type="button"
                        className="rn-link"
                        disabled={correctAddress.isPending}
                        onClick={() => setCorrecting(correcting === inv.id ? null : inv.id)}
                      >
                        Fix address
                      </button>
                    ) : null}
                    <button
                      type="button"
                      className="rn-link"
                      disabled={revoke.isPending}
                      // Destructive, so it asks first (the same pattern the
                      // person screen uses for suspend/deactivate). One click
                      // on the wrong row of this list used to kill a link
                      // somebody may already be holding, with no way back but
                      // re-inviting them.
                      onClick={() => {
                        if (
                          window.confirm(
                            `Revoke the invitation to ${inv.email}? Any link already sent stops working, and they will need a new invitation to join.`,
                          )
                        ) {
                          revoke.mutate(inv.id);
                        }
                      }}
                    >
                      Revoke
                    </button>
                  </div>
                ) : null}
              </li>
            ))}
          </ul>
        )}
      </SettingsCard>

      {/* `roleOptions.data ?? []` gave the invite form an empty role select on a
          failed read, and an administrator cannot invite anybody without a
          role — a screen that looks usable and quietly is not. */}
      {canManage ? (
        <InviteCard
          roles={roleOptions.data ?? []}
          rolesFailed={roleOptions.isError && roleOptions.data === undefined}
          onRetryRoles={() => void roleOptions.refetch()}
        />
      ) : null}
    </div>
  );
}

/**
 * What the invitation email actually did.
 *
 * Without this the list can only say "invited", which is equally true of a row
 * whose mail was delivered, one whose address bounced, one sitting in a
 * deployment with no email provider configured, and one no worker ever picked
 * up. Those are four different problems with four different fixes, and the
 * administrator looking at this screen is the person who can act on the
 * difference.
 *
 * `pending` is now narrow enough to deserve its participle (F108): it means a
 * worker genuinely has the message. The case that used to hide behind it — a
 * delivery nothing ever took, which no later event corrects — is `not_queued`,
 * and it says so instead of saying "Sending…" for a week.
 */
const DELIVERY_COPY: Record<
  UsersNS.InvitationDelivery,
  { label: string; tone: string; hint?: string }
> = {
  sent: { label: 'Email sent', tone: 'var(--color-good-deep)' },
  pending: { label: 'Sending…', tone: 'var(--color-soft)' },
  not_queued: {
    label: 'Not sent — nothing is running to send it',
    tone: 'var(--color-urgent-deep)',
    hint: 'No background worker was running when this was requested, so nothing picked it up and nothing will until one is started. The invitation is saved; use Resend once it is.',
  },
  failed: {
    label: 'Email failed',
    tone: 'var(--color-urgent-deep)',
    hint: 'We could not deliver to this address. Check the spelling, then resend.',
  },
  not_configured: {
    label: 'Not sent — email is not configured',
    tone: 'var(--color-urgent-deep)',
    hint: 'This deployment has no email provider, so the invitation was created but never sent. Nothing is wrong with the address.',
  },
  unknown: { label: 'Delivery unknown', tone: 'var(--color-soft)' },
};

/**
 * The evidence UNDER the coarse word, at the point somebody decides to resend.
 *
 * The five API fields this reads have been on the wire since L70 and were read
 * by nothing (`docs/INVITATION-RECOVERY.md` §8 finding 1). They matter here and
 * nowhere else: this row is where an administrator chooses between pressing
 * Resend, fixing the address, and going to look at the deployment, and the
 * coarse enum alone cannot tell those three apart.
 *
 *   - `pending` + `attempts === 0` + a request-time days old is a message
 *     nothing has ever picked up. It renders as "Sending…", which is the one
 *     sentence that makes a person press Resend again.
 *   - `failed` without the failure CODE sends somebody to check the spelling of
 *     an address that was never the problem: `http_422` is the provider refusing
 *     the message and only `invalid_address` is the address. A NETWORK failure
 *     never writes a code at all — the row stays `pending` and retries — so a
 *     code like `fetch_failed` is not a thing this screen will ever read,
 *     despite the column's own migration comment once offering it as an example
 *     (F124/F155: that example seeded the same error into two docs, this one
 *     included).
 *
 * Deliberately not a badge. It is what the row already said, with the fact that
 * contradicts it printed underneath.
 */
function deliveryEvidence(invitation: UsersNS.Invitation): string[] {
  const lines: string[] = [];
  const { deliveryAttempts: attempts, deliveryRequestedAt: requestedAt } = invitation;
  if (requestedAt) {
    lines.push(`Requested ${new Date(requestedAt).toLocaleString()}`);
  }
  // `attempts` is committed BEFORE the network call, so zero is not "the send
  // failed" — it is "nothing ever looked at this row". Only said where it
  // contradicts the label: `not_queued` and `not_configured` already explain
  // themselves, and repeating it there is noise on a row that is already right.
  if (attempts === 0 && invitation.delivery === 'pending') {
    lines.push(
      'No delivery has been attempted yet — nothing has picked this up. Pressing Resend again will not change that.',
    );
  } else if (attempts != null && attempts > 0) {
    lines.push(`${attempts} delivery ${attempts === 1 ? 'attempt' : 'attempts'}`);
  }
  // Whenever a code exists, not only on `failed` (F158). Today every code this
  // projection can carry arrives with status `failed`, but that is a fact about
  // the email domain's current writers, not about this screen — a code gated on
  // one status silently vanishes the day a writer pairs it with another, which
  // is exactly how `identity_provider_disabled` shipped invisible on the
  // password-reset path. The cause is evidence wherever it is recorded.
  if (invitation.deliveryFailureCode) {
    lines.push(`Provider reported: ${invitation.deliveryFailureCode}`);
  }
  return lines;
}

function DeliveryNote({ invitation }: { invitation: UsersNS.Invitation }) {
  const copy = DELIVERY_COPY[invitation.delivery] ?? DELIVERY_COPY.unknown;
  const evidence = deliveryEvidence(invitation);
  return (
    <div className="rn-muted" style={{ color: copy.tone, marginTop: 2 }}>
      {copy.label}
      {invitation.deliveredAt ? ` · ${new Date(invitation.deliveredAt).toLocaleString()}` : ''}
      {copy.hint ? <div style={{ color: 'var(--color-soft)' }}>{copy.hint}</div> : null}
      {evidence.map((line) => (
        <div key={line} style={{ color: 'var(--color-soft)' }}>
          {line}
        </div>
      ))}
      {invitation.attachesToExistingMember ? (
        /**
         * The §7 review, on screen instead of in SQL.
         *
         * Acceptance upserts on `(org_id, lower(email))`, so an invitation
         * addressed to an address a member already holds GRANTS that member the
         * invited role rather than creating anybody. That is legitimate when
         * somebody is deliberately adding a second role, and it is an
         * escalation when it is not — the two read identically from outside,
         * which is exactly why it is said out loud here and not acted on.
         */
        <div style={{ color: 'var(--color-urgent-deep)' }}>
          Accepting this would add the role to an account that already exists in this organization,
          not create a new member. Worth checking that is what was meant.
        </div>
      ) : null}
    </div>
  );
}

/**
 * Correcting the address on a pending invitation (F95).
 *
 * One field, prefilled with what is there now, because this is a repair of a
 * typo and retyping the whole address from scratch is how the second typo
 * happens. The sentence above it says what the button actually does, including
 * the part that is easy to miss: the old link stops working.
 */
function CorrectAddressForm({
  currentEmail,
  pending,
  onCancel,
  onSubmit,
}: {
  currentEmail: string;
  pending: boolean;
  onCancel: () => void;
  onSubmit: (email: string) => void;
}) {
  const [email, setEmail] = useState(currentEmail);
  const unchanged = email.trim().toLowerCase() === currentEmail.trim().toLowerCase();
  return (
    <form
      style={{ marginTop: 'var(--space-8)' }}
      onSubmit={(e) => {
        e.preventDefault();
        if (!unchanged) onSubmit(email.trim().toLowerCase());
      }}
    >
      <label className="rn-muted" htmlFor={`fix-address-${currentEmail}`}>
        Correct the address
      </label>
      <input
        id={`fix-address-${currentEmail}`}
        type="email"
        required
        value={email}
        onChange={(e) => setEmail(e.target.value)}
        style={{ display: 'block', marginTop: 2 }}
      />
      <p className="rn-muted" style={{ marginBottom: 0 }}>
        This invitation will point at the new address and be emailed there. Any link already sent to{' '}
        {currentEmail} stops working immediately.
      </p>
      <div style={{ display: 'flex', gap: 'var(--space-8)', marginTop: 'var(--space-8)' }}>
        <button type="submit" className="rn-link" disabled={pending || unchanged}>
          Send to this address
        </button>
        <button type="button" className="rn-link" onClick={onCancel}>
          Cancel
        </button>
      </div>
    </form>
  );
}

interface EmailAttemptNotice {
  headline: string;
  lines: string[];
  bad: boolean;
}

/** One rendering for both notices, so create and resend cannot drift apart visually. */
function NoticeBlock({ notice, label }: { notice: EmailAttemptNotice; label: string }) {
  return (
    <div role="status" aria-label={label} style={{ marginBottom: 0 }}>
      <p
        className="rn-muted"
        style={{
          color: notice.bad ? 'var(--color-urgent-deep)' : 'var(--color-good-deep)',
          marginBottom: 0,
        }}
      >
        {notice.headline}
      </p>
      {notice.lines.map((line) => (
        <p key={line} className="rn-muted" style={{ marginBottom: 0 }}>
          {line}
        </p>
      ))}
    </div>
  );
}

/**
 * What to say after the API answers about an invitation email — and what NOT to
 * say (F94 for create, F105 for resend).
 *
 * The old create copy was `result.delivery === 'queued' ? 'Invitation sent.' : …`.
 * `'queued'` means the durable jobs executor accepted the job; it says nothing
 * about whether a mail provider exists to send it. On a deployment with a worker
 * and no provider — which is this one — every invitation answered 'queued', so
 * "Invitation sent." was false for every invitation ever created, and it was
 * false to the one person who would otherwise have gone looking.
 *
 * Both responses now carry the two facts separately, and so does this notice.
 * They are not interchangeable: "no worker is running" is fixed by starting one,
 * "no provider is configured" is fixed by setting a credential, and an
 * administrator handed a single blurred word goes and fixes the wrong one. The
 * happy case therefore claims only what is true at that instant — the message
 * was queued, with something configured to send it — and points at the pending
 * list, which is where "Email sent" is asserted only after a provider has
 * genuinely accepted it.
 *
 * ONE function for both buttons, deliberately. The resend route was the last
 * place that could still over-claim, and the way that happens again is two
 * copies of this reasoning drifting apart. Only the headlines differ, because
 * only the underlying event does: create made an invitation, resend did not.
 */
function emailAttemptNotice(
  result: { delivery: UsersNS.InviteDeliveryStatus; provider: UsersNS.InviteProviderStatus },
  headlines: { good: string; bad: string; saved: string },
): EmailAttemptNotice {
  const configured = result.provider === 'configured';
  const queued = result.delivery === 'queued';

  if (configured && queued) {
    return {
      headline: headlines.good,
      lines: [
        // No "above"/"below": this notice renders under the invite form AND
        // beside a pending row, and a direction that is right in one place is
        // wrong in the other.
        'Nothing has been delivered yet. “Pending invitations” says “Email sent” once the provider has genuinely accepted it.',
      ],
      bad: false,
    };
  }

  const lines: string[] = [];
  if (!configured) {
    lines.push(
      'This deployment has no email provider configured, so no invitation can be emailed until one is set up. That is a deployment setting — nothing is wrong with the address.',
    );
  }
  if (result.delivery === 'recorded') {
    lines.push(
      'No background worker is running, so the message has not even been picked up to send.',
    );
  } else if (result.delivery === 'duplicate') {
    lines.push(
      'An email for this invitation had already been requested, so nothing new was queued.',
    );
  }
  lines.push(headlines.saved);
  return { headline: headlines.bad, lines, bad: true };
}

function inviteNotice(result: UsersNS.InviteResult): EmailAttemptNotice {
  return emailAttemptNotice(result, {
    good: 'Invitation created and queued for email.',
    bad: 'Invitation created — no email has been sent.',
    saved: 'The invitation itself is saved. Use Resend above once that is fixed.',
  });
}

/**
 * The resend half (F105).
 *
 * Resend is the button an administrator reaches for BECAUSE the first attempt
 * did not arrive. Until the route carried `provider`, the screen could only have
 * repeated the same over-claim to the same person a second time — so the copy
 * here is held to exactly the standard the create notice is, and it never says
 * an email went out.
 */
function resendNotice(result: UsersNS.ResendResult): EmailAttemptNotice {
  return emailAttemptNotice(result, {
    good: 'Queued for email again.',
    bad: 'Nothing was sent.',
    saved: 'The invitation is untouched and still valid; try Resend again once that is fixed.',
  });
}

/**
 * The corrected-address half (F95).
 *
 * The correction ITSELF succeeded whatever the email did — the invitation now
 * points at the new address and the old link is dead — and the copy has to say
 * that even in the unhappy branch, or an administrator reads "Nothing was sent"
 * and tries the whole repair again against a row that is already correct.
 */
function correctedAddressNotice(result: UsersNS.ResendResult): EmailAttemptNotice {
  return emailAttemptNotice(result, {
    good: 'Address corrected, and queued for email.',
    bad: 'Address corrected — but nothing was sent.',
    saved:
      'The correction itself is saved: this invitation now points at the new address, and any link already sent to the old one no longer works.',
  });
}

function InviteCard({
  roles,
  rolesFailed,
  onRetryRoles,
}: {
  roles: { id: string; key: string; name: string }[];
  rolesFailed: boolean;
  onRetryRoles: () => void;
}) {
  const qc = useQueryClient();
  const [email, setEmail] = useState('');
  const [name, setName] = useState('');
  const [roleKey, setRoleKey] = useState('');
  const [error, setError] = useState<string | null>(null);
  const [notice, setNotice] = useState<ReturnType<typeof inviteNotice> | null>(null);

  const invite = useMutation({
    mutationFn: () =>
      users.invite({ email, roleKey, ...(name.trim() ? { name: name.trim() } : {}) }),
    onSuccess: (result) => {
      setEmail('');
      setName('');
      setRoleKey('');
      setError(null);
      // Say what actually happened, from BOTH facts the response carries — see
      // inviteNotice. Neither "the job was accepted" nor "a provider exists"
      // implies the other, and the administrator reading this is the person who
      // would otherwise stop watching for an email that is never coming.
      setNotice(inviteNotice(result));
      void qc.invalidateQueries({ queryKey: ['invitations', 'list'] });
    },
    onError: (e: unknown) => {
      setNotice(null);
      setError(e instanceof Error ? e.message : 'Invitation failed.');
    },
  });

  return (
    <SettingsCard>
      <h2 className="rn-card-title">Invite someone</h2>
      {/* "They'll get an email link." was the same promise as the old button
          verb, in prose, and equally unsupported: creating an invitation
          records it and requests an email, and whether one is ever sent depends
          on this deployment having a worker and a mail provider. The result
          notice says which. */}
      <p className="rn-muted" style={{ margin: 'var(--space-6) 0 var(--space-12)' }}>
        The invite is tied to the role you choose. Creating it queues an email — “Pending
        invitations” is where you can see whether one actually went out.
      </p>
      {/* Labelled through `SettingsField`, the same mechanism every other
          Settings form goes through. These three carried placeholders only, and
          a placeholder is not a label: it vanishes the moment somebody types,
          and it never reaches a screen reader as the control's name at all —
          the one form that creates accounts was the one form with three
          nameless controls. Placeholders stay as example text, which is the
          job they can actually do. */}
      <div style={{ display: 'grid', gap: 'var(--space-10)', maxWidth: 420 }}>
        <SettingsField label="Full name (optional)">
          <input
            style={{ marginBottom: 0 }}
            type="text"
            maxLength={120}
            value={name}
            onChange={(e) => setName(e.target.value)}
          />
        </SettingsField>
        <SettingsField label="Email address">
          <input
            style={{ marginBottom: 0 }}
            type="email"
            placeholder="name@example.org"
            value={email}
            onChange={(e) => setEmail(e.target.value)}
          />
        </SettingsField>
        <SettingsField label="Role">
          <select
            style={{ marginBottom: 0 }}
            value={roleKey}
            onChange={(e) => setRoleKey(e.target.value)}
          >
            <option value="" disabled>
              Choose a role…
            </option>
            {roles.map((r) => (
              <option key={r.id} value={r.key}>
                {r.name}
              </option>
            ))}
          </select>
        </SettingsField>
        {rolesFailed ? (
          <p className="rn-muted" role="status" style={{ marginBottom: 0 }}>
            We couldn’t load your organization’s roles, so there is nothing to choose from and an
            invitation cannot be sent yet.{' '}
            <button type="button" className="rn-link" onClick={onRetryRoles}>
              Try again
            </button>
          </p>
        ) : null}
        {/* The verb the response supports (F106). This read "Send invitation" /
            "Sending…" while the notice underneath said the invitation was
            created and no email had been sent — the button promising the thing
            the sentence beneath it withdraws, which is the same over-claim F94
            removed from the notice, left standing in the control that starts it.
            The endpoint creates an invitation and requests an email; only the
            worker can send one, and only "Pending invitations" reports that. */}
        <button
          type="button"
          className="signin-devbtn"
          style={{ width: 'auto', padding: 'var(--space-10) var(--space-16)' }}
          disabled={invite.isPending || !email.trim() || !roleKey}
          onClick={() => invite.mutate()}
        >
          {invite.isPending ? 'Creating…' : 'Create invitation'}
        </button>
        {notice ? <NoticeBlock notice={notice} label="Invitation result" /> : null}
        {error ? (
          <p className="rn-muted" style={{ color: 'var(--color-urgent-deep)' }}>
            {error}
          </p>
        ) : null}
      </div>
    </SettingsCard>
  );
}
